import "./Login.css";
import "./AuthAction.css";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { auth } from "../base.js";
import { AiOutlineLeft as BackIcon } from "react-icons/ai";
import { SyncLoader } from "react-spinners";
import Button from "../components/Button";

// Branded replacement for Firebase's default action page at
// <authDomain>/__/auth/action. Firebase appends ?mode=...&oobCode=...&apiKey=...
// to whatever "action URL" the email template points at; pointing that URL here
// (route /auth/action in App.js) lets us render the reset/verify flows in our
// own UI instead of Google's generic page. The action-code APIs work with just
// the project apiKey (from base.js), so this page functions even when the link
// is opened on a device where the user isn't signed in.
//
// Covers mode=resetPassword and mode=verifyEmail (what we send today). Other
// modes (recoverEmail, verifyAndChangeEmail) fall through to a graceful notice
// rather than a broken screen.

// Maps the oobCode failure codes shared by all action-code APIs to friendly
// copy. Expired/invalid links are the common case — both mean "ask for a new
// one" — so we steer the user back rather than showing a raw Firebase error.
function actionErrorMessage(error) {
  switch (error?.code) {
    case "auth/expired-action-code":
      return "This link has expired. Please request a new one and try again.";
    case "auth/invalid-action-code":
      return (
        "This link is invalid or has already been used. If you've already " +
        "completed this step, you can simply sign in."
      );
    case "auth/user-disabled":
      return "This account has been disabled. Please contact support.";
    case "auth/user-not-found":
      return "We couldn't find an account for this link.";
    case "auth/weak-password":
      return "Your password must be at least 6 characters.";
    default:
      return "Something went wrong. Please try again.";
  }
}

const Header = ({ title, subtitle }) => {
  const navigate = useNavigate();
  return (
    <div id="loginHeader">
      <div id="loginHeaderText">
        <h1 style={{ fontFamily: "NexaBold", fontSize: "26px" }}>{title}</h1>
        {subtitle && (
          <p
            style={{
              fontFamily: "Mulish",
              fontSize: "18px",
              fontWeight: "700",
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      <div onClick={() => navigate("/login")} id="backButtonContainer">
        <BackIcon size={20} />
        <p style={{ paddingTop: "14px", paddingLeft: "5px" }}>Sign In</p>
      </div>
    </div>
  );
};

// Centred status message reused by the loading/success/error states.
const Notice = ({ children }) => (
  <p
    style={{
      fontFamily: "Mulish",
      fontSize: "16px",
      fontWeight: "700",
      paddingTop: "1rem",
      textAlign: "center",
    }}
  >
    {children}
  </p>
);

const AuthAction = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode = params.get("mode");
  const oobCode = params.get("oobCode");

  // "working" while we validate/apply the code; "resetForm" once a reset code is
  // verified; "success" / "error" terminal states.
  const [phase, setPhase] = useState("working");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // applyActionCode (verifyEmail) is single-use: a second call fails with
  // invalid-action-code. React 18 StrictMode runs effects twice in dev, so guard
  // with a ref (persists across the dev double-invoke) to avoid a false error.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) {
      return;
    }
    ran.current = true;

    if (!oobCode) {
      setPhase("error");
      setMessage(
        "This link is missing its security code. Please use the most recent email we sent you.",
      );
      return;
    }

    if (mode === "verifyEmail") {
      applyActionCode(auth, oobCode)
        .then(async () => {
          // If the link is opened in the same session the user signed up in,
          // refresh the ID token so its email_verified claim is current for
          // claimAccess (Auth.js). Harmless — and skipped — when signed out.
          if (auth.currentUser) {
            try {
              await auth.currentUser.reload();
              await auth.currentUser.getIdToken(true);
            } catch (e) {
              // Token race / offline — the VerifyEmail poll screen still catches up.
            }
          }
          setPhase("success");
          setMessage(
            "Your email is verified. You can now sign in and start using Piggyback.",
          );
        })
        .catch((e) => {
          setPhase("error");
          setMessage(actionErrorMessage(e));
        });
      return;
    }

    if (mode === "resetPassword") {
      // Validate the code up front (also surfaces expired/used links before we
      // show the form) and recover the target email to confirm it to the user.
      verifyPasswordResetCode(auth, oobCode)
        .then((addr) => {
          setEmail(addr);
          setPhase("resetForm");
        })
        .catch((e) => {
          setPhase("error");
          setMessage(actionErrorMessage(e));
        });
      return;
    }

    // recoverEmail / verifyAndChangeEmail / unknown — we don't send these, so
    // don't pretend to handle them; point the user back to a known-good path.
    setPhase("error");
    setMessage(
      "This link isn't supported here. Please sign in, or request a new email.",
    );
  }, [mode, oobCode]);

  const handleResetSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setFormError("");
      if (password.length < 6) {
        setFormError("Your password must be at least 6 characters.");
        return;
      }
      if (password !== confirm) {
        setFormError("The passwords don't match.");
        return;
      }
      setSubmitting(true);
      try {
        await confirmPasswordReset(auth, oobCode, password);
        setPhase("success");
        setMessage(
          "Your password has been reset. You can now sign in with your new password.",
        );
      } catch (e) {
        // An expired/used code can only surface now if the user lingered on the
        // form; send them back to request a fresh link.
        if (e.code === "auth/weak-password") {
          setFormError(actionErrorMessage(e));
          setSubmitting(false);
          return;
        }
        setPhase("error");
        setMessage(actionErrorMessage(e));
      }
    },
    [oobCode, password, confirm],
  );

  if (phase === "working") {
    return (
      <div className="authAction">
        <Header title="One moment…" subtitle="Confirming your link." />
        <div id="loginBody">
          <SyncLoader color={"#e7b33e"} loading={true} size={15} />
        </div>
      </div>
    );
  }

  if (phase === "resetForm") {
    return (
      <div className="authAction">
        <Header
          title="Choose a new password"
          subtitle={`Set a new password for ${email}.`}
        />
        <form id="loginForm" onSubmit={handleResetSubmit}>
          <div className="loginFormRowContainer">
            <div className="loginInputContainer">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: "300px" }}
                type="password"
                name="newPassword"
                autoComplete="new-password"
              />
            </div>
            <div className="loginInputContainer">
              <label htmlFor="confirmPassword">Confirm password</label>
              <input
                id="confirmPassword"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={{ width: "300px" }}
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
              />
            </div>
          </div>
          {formError && <p style={{ color: "red" }}>{formError}</p>}
          <Button type="submit" disabled={submitting || !password || !confirm}>
            {submitting ? "Saving…" : "Save new password"}
          </Button>
        </form>
      </div>
    );
  }

  // success | error
  return (
    <div className="authAction">
      <Header
        title={phase === "success" ? "All set" : "Something went wrong"}
      />
      <div id="loginBody">
        <Notice>{message}</Notice>
        <Button
          onClick={() => navigate("/login")}
          style={{ alignSelf: "center" }}
        >
          Continue to sign in
        </Button>
      </div>
    </div>
  );
};

export default AuthAction;
